import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, Sparkles, Loader2, Copy } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { ModelPicker } from "@/components/common/ModelPicker";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

function CopyField({ label, value, testid }) {
    const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
                <button onClick={copy} data-testid={`copy-${testid}`} className="text-muted-foreground hover:text-volt"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <div className="bg-obsidian border border-border rounded-sm p-3 font-mono text-xs text-foreground/90 whitespace-pre-wrap">{value}</div>
        </div>
    );
}

export function PublishKit() {
    const [packages, setPackages] = useState(null);
    const [clips, setClips] = useState([]);
    const [clipId, setClipId] = useState("none");
    const [topic, setTopic] = useState("");
    const [model, setModel] = useState("gpt-5.2");
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null);

    const load = async () => {
        const [c, cl] = await Promise.all([api.get("/content"), api.get("/clips")]);
        setPackages(c.data.packages);
        setClips(cl.data.clips);
        if (c.data.packages[0]) setActive(c.data.packages[0]);
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setBusy(true);
        try {
            const body = { model, topic: topic || undefined, clip_id: clipId !== "none" ? clipId : undefined };
            const { data } = await api.post("/content/generate", body);
            if (data.generation_status === "fallback") toast.warning(data.generation_note || "AI was unavailable; a deterministic package was created.");
            else toast.success("Content package generated.");
            setActive(data); setTopic("");
            const c = await api.get("/content"); setPackages(c.data.packages);
        } catch { toast.error("Generation failed."); }
        finally { setBusy(false); }
    };

    if (!packages) return <div className="py-16 text-center text-muted-foreground font-mono text-sm">Loading publish kit…</div>;

    return (
        <div>
            <div className="bg-surface border border-border rounded-sm p-5 mb-6">
                <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
                    <div className="flex-1">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Topic / Hook</label>
                        <Input data-testid="content-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Insane 1v4 clutch in final circle"
                            className="bg-obsidian border-border rounded-sm h-10 mt-1.5" />
                    </div>
                    <Select value={clipId} onValueChange={setClipId}>
                        <SelectTrigger className="w-full lg:w-[200px] h-10 bg-obsidian border-border rounded-sm" data-testid="content-clip"><SelectValue placeholder="Attach clip" /></SelectTrigger>
                        <SelectContent className="bg-surface2 border-border">
                            <SelectItem value="none">No clip</SelectItem>
                            {clips.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <ModelPicker value={model} onChange={setModel} />
                    <button data-testid="generate-content" onClick={generate} disabled={busy}
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
                    </button>
                </div>
            </div>

            {packages.length === 0 ? (
                <EmptyState icon={Youtube} title="No content packages yet" subtitle="Generate a YouTube package from one of your clips." />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="space-y-2">
                        {packages.map((p) => (
                            <button key={p.id} onClick={() => setActive(p)} data-testid={`content-pkg-${p.id}`}
                                className={`w-full text-left p-3 rounded-sm border transition-colors ${active?.id === p.id ? "border-volt bg-volt/10" : "border-border bg-surface hover:border-white/25"}`}>
                                <div className="flex items-center gap-2"><Youtube className="w-4 h-4 text-alert shrink-0" /><span className="text-sm text-white truncate">{p.title}</span></div>
                            </button>
                        ))}
                    </div>

                    {active && (
                        <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6 space-y-4" data-testid="content-detail">
                            {active.generation_status === "fallback" && (
                                <div className="rounded-sm border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                                    Deterministic publish kit shown — the selected AI provider was unavailable.
                                </div>
                            )}
                            <CopyField label="YouTube Title" value={active.youtube_title} testid="title" />
                            <CopyField label="Description" value={active.description} testid="desc" />
                            <CopyField label="Hashtags" value={(active.hashtags || []).join(" ")} testid="hashtags" />
                            <div>
                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Chapters</span>
                                <div className="bg-obsidian border border-border rounded-sm p-3 mt-1.5 font-mono text-xs space-y-1">
                                    {active.chapters?.map((c, i) => <div key={i} className="text-foreground/90"><span className="text-volt">{c.time}</span> {c.title}</div>)}
                                </div>
                            </div>
                            <CopyField label="Narration Script" value={active.narration_script} testid="script" />
                            <div>
                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Short / Reel Suggestions</span>
                                <ul className="mt-1.5 space-y-1.5">
                                    {active.short_suggestions?.map((s, i) => <li key={i} className="text-sm text-foreground/90 flex gap-2"><span className="text-volt">▸</span>{s}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
